/**
 * `<MarketChart>` — candlesticks of the **underlying**, with the market drawn
 * over them.
 *
 * **The chart is BTC (or ETH), not the market's probability.** That is the spec
 * owner's decision, taken 2026-08-16 and recorded as `06` §F19, and it overrules
 * `components.md` §2.10 as originally written. The reasoning is the product's,
 * not the chart's: a trader deciding whether this minute settles UP is asking
 * where the underlying goes relative to a strike, and a probability track is a
 * derived, lagging restatement of that question. The strike lines make the
 * payoff readable directly off the price.
 *
 * **Why `lightweight-charts` and not the uPlot line this replaced.** A
 * candlestick chart a trader can work from needs a real time scale — pan, zoom,
 * a fitted right price scale, a crosshair that snaps to a bar, an interval
 * ladder — and none of that is chart drawing, it is a charting *product*. uPlot
 * gives scales and axes; this gives the terminal. Decision (b) of the Phase 4
 * brief went the other way on §2.10's original geometry and is reversed here.
 *
 * **The market is drawn as primitives over the price**, because that is the part
 * no charting library has: each market's strike is a labelled horizontal line at
 * the price it fixes at, and every expiry is a vertical mark on the time axis. A
 * trader reading this chart can see the strike they are trading against, where
 * the current minute ends, and what the last several minutes settled at. Nothing
 * is *shaded* — `chart-bands.ts` says why a fill here is a fill over the
 * countdown.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineStyle,
  createChart,
  type CandlestickData,
  type IChartApi,
  type IPriceLine,
  type ISeriesApi,
  type Time,
  type AutoscaleInfo,
  type UTCTimestamp,
} from 'lightweight-charts'
import { cx } from '@/lib/cx'
import { Glyph } from '@/components/primitives'
import { nextRadioIndex, radioTabIndex } from '@/components/primitives/radio-group'
import { CHART_FLOOR_BPS, observationFrom, toNumber } from '@/domain/price'
import { MarketBandsPrimitive, type MarketBand } from './chart-bands'
import type { components } from '@/api/generated/rest'

/** `openapi.IndexInterval` — what `GET /index/{symbol}/candles` will accept. */
type IndexInterval = components['parameters']['IndexInterval']

/**
 * The interval ladder — `openapi.IndexInterval`, plus one entry that is not an
 * interval at all.
 *
 * `15s` and below exist because the product's own object is sixty seconds long;
 * `1h` and above exist because the *index* is continuous and a trader arriving
 * cold wants to know what BTC has been doing. §F19 split this from the market's
 * own `1s`/`5s`, which is still the right answer for a market that lives a
 * minute.
 *
 * **`market` is a viewport, not an interval**, and it is the default. It draws
 * the same `1s` candles of the underlying with the visible range pinned to the
 * market's own `[anchor_ms, expiry_at_ms)` — the sixty seconds the user is
 * actually trading, and nothing either side of it. Every other option shows a
 * rolling window that knows nothing about the market, which on a sixty-second
 * product puts the minute that decides the payoff in the right-hand third of
 * the pane with four dead markets beside it.
 *
 * It is deliberately **not** the market's own price. `GET /markets/{id}/candles`
 * exists and serves the outcome price in cents, and §F19 settled that the chart
 * is the underlying: the probability is the strip, `<OutcomeToggle>` and
 * `<StrikeMeter>`. Drawing the index is also what keeps the strike line, the
 * expiry rule meaningful — they are prices on this axis.
 */
export const TIMEFRAMES = ['market', '1s', '5s', '15s', '1m', '5m', '15m', '1h'] as const
export type Timeframe = (typeof TIMEFRAMES)[number]

/**
 * The bucket a live tick extends, per option.
 *
 * `market` buckets at a second because it *is* a second — the option changes
 * where the pane looks, never what it is looking at.
 */
export const TIMEFRAME_MS: Record<Timeframe, number> = {
  market: 1_000,
  '1s': 1_000,
  '5s': 5_000,
  '15s': 15_000,
  '1m': 60_000,
  '5m': 300_000,
  '15m': 900_000,
  '1h': 3_600_000,
}

/**
 * The `interval` each option asks the venue for.
 *
 * **`'market'` must never reach the wire.** `openapi.IndexInterval` has no such
 * member, so `GET /index/{symbol}/candles?interval=market` is an
 * `INVALID_PARAMETER` — and the request is built by passing the timeframe
 * straight through (`use-index-candles.ts`), which is exactly the shape that
 * would ship it. Every caller resolves through here first.
 *
 * The other half of its job is the cache: `market` and `1s` resolve to one key,
 * so switching between them is free rather than two fetches of identical bars.
 */
export const INDEX_INTERVAL: Record<Timeframe, IndexInterval> = {
  market: '1s',
  '1s': '1s',
  '5s': '5s',
  '15s': '15s',
  '1m': '1m',
  '5m': '5m',
  '15m': '15m',
  '1h': '1h',
}

/** Bars fetched per timeframe change. The `Limit` parameter's ceiling is 500;
 *  300 is several screens of scroll-back at any interval. */
export const HISTORY_BARS = 300

/** Bars visible on open. The rest are a pan away — which is the difference
 *  between a chart and a picture of one. */
export const VISIBLE_BARS = 90

/** Bars of empty pane kept to the right of the newest bar, so the forming bar
 *  never sits against the price axis. */
export const RIGHT_HEADROOM_BARS = 4

export interface ChartCandle {
  open_time_ms?: number
  open?: string
  high?: string
  low?: string
  close?: string
  is_closed?: boolean
}

export interface MarketChartProps {
  symbol: string
  /** Decimal places for the price axis — `config.symbols[].price_decimals`. */
  priceDecimals?: number
  timeframe: Timeframe
  onTimeframeChange: (timeframe: Timeframe) => void
  /** Oldest-first history for `timeframe`, from `GET /index/{symbol}/candles`. */
  history: readonly ChartCandle[]
  /** `true` while that fetch is in flight, so the pane can say so. */
  loading?: boolean
  /** The latest `index.{symbol}` payload — extends the forming bar. */
  index: unknown
  /** Every market whose strike and window belong on screen, oldest first. */
  bands: readonly MarketBand[]
  /** The market the page is pointed at, for the emphasised strike line. */
  currentMarketId: string | null
  /**
   * Painted **behind** the candles, filling the pane.
   *
   * A slot rather than a prop bundle, on `<AppShell>`'s reasoning: the chart
   * draws prices and knows nothing about the market's state machine, and the
   * one thing that wants to sit back there — the countdown — needs the clock,
   * the lifecycle state and three timestamps. The caller composes it; this
   * component gives it a layer and gets out of the way.
   */
  underlay?: ReactNode
  /**
   * The live market's own span, `[anchor_ms, expiry_at_ms]`.
   *
   * Read by the `market` timeframe and ignored by every other one. Optional
   * because a caller with no market to point at — the gallery — still gets the
   * ladder it has always had; without them `market` falls back to the rolling
   * window, which is a worse render than the one asked for and a better one
   * than an empty pane.
   */
  windowStartMs?: number
  windowEndMs?: number
  stale?: boolean
  className?: string
}

export function MarketChart({
  symbol,
  priceDecimals = 2,
  timeframe,
  onTimeframeChange,
  history,
  loading,
  index,
  bands,
  currentMarketId,
  underlay,
  windowStartMs,
  windowEndMs,
  stale,
  className,
}: MarketChartProps) {
  const hostRef = useRef<HTMLDivElement>(null)
  const timeframeRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null)
  const primitiveRef = useRef<MarketBandsPrimitive | null>(null)
  const priceLinesRef = useRef<IPriceLine[]>([])
  /** The bar currently being formed by live ticks, so an update never rewrites
   *  a closed bar. */
  const formingRef = useRef<CandlestickData<Time> | null>(null)
  /** The live market's strike, for the autoscale below. A ref rather than
   *  state: the price scale reads it during a draw, not during a render. */
  const strikeRef = useRef<number | null>(null)
  /** The last range applied. The viewport effect runs at the index's rate and
   *  almost always computes the same numbers; this is what keeps it from
   *  asking the time scale to relayout five times a second for nothing. */
  const rangeRef = useRef<{ from: number; to: number } | null>(null)
  const [tokens, setTokens] = useState<Tokens | null>(null)
  const [legend, setLegend] = useState<Legend | null>(null)
  /**
   * The axis labels' size, in pixels — `caption`, resolved.
   *
   * A canvas cannot read a custom property, and since the scale unit
   * (`--ud-u`, 2026-10-10) `--ud-text-caption-size` is a multiple of it that
   * grows from 1440px up, so a literal `11` would leave the axis at 11px on a
   * 4K screen whose every other label had grown. Re-read on resize, and applied
   * to the live chart rather than rebuilding it.
   */
  const [axisFontPx, setAxisFontPx] = useState(11)

  // Canvas colours are read from the live DOM and re-read when the theme
  // attribute changes. A canvas cannot inherit a custom property, and the
  // design system forbids a hex literal in a component — so this is the
  // substitute, and without the observer the chart keeps the palette it was
  // born with while the rest of the page switches.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const read = () => setTokens(readTokens(host))
    read()
    const observer = new MutationObserver(read)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-cvd'],
    })
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const read = () => setAxisFontPx(pxOf(host, '--ud-text-caption-size', 11))
    read()
    window.addEventListener('resize', read)
    return () => window.removeEventListener('resize', read)
  }, [])

  // ---------------------------------------------------------------------
  // The chart, created once per theme
  // ---------------------------------------------------------------------

  useEffect(() => {
    const host = hostRef.current
    if (!host || !tokens) return

    const chart = createChart(host, {
      autoSize: true,
      layout: {
        // **Transparent, and that is what makes the underlay an underlay.**
        // The canvas is opaque by default, so anything behind it is behind a
        // wall. With it clear, the panel's own `bg-bg-surface` shows through —
        // and so does the countdown layer between the two. The candles keep
        // full contrast because nothing is drawn *over* them.
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: tokens.textTertiary,
        fontSize: 11,
        fontFamily: tokens.mono,
        attributionLogo: false,
      },
      grid: {
        // Horizontal only. The time axis of a 60-second product is dense
        // enough without vertical rules (§2.10).
        horzLines: { color: tokens.borderSubtle },
        vertLines: { visible: false },
      },
      rightPriceScale: {
        borderColor: tokens.borderDefault,
        scaleMargins: { top: 0.12, bottom: 0.12 },
      },
      leftPriceScale: { visible: false },
      timeScale: {
        borderColor: tokens.borderDefault,
        timeVisible: true,
        secondsVisible: true,
        rightOffset: 6,
        barSpacing: 8,
      },
      crosshair: {
        // Magnet: the crosshair snaps to a bar rather than floating between
        // two, which is what makes the readout trustworthy.
        mode: CrosshairMode.Magnet,
        vertLine: { color: tokens.borderStrong, width: 1, style: LineStyle.Dotted, labelBackgroundColor: tokens.bgRaised },
        horzLine: { color: tokens.borderStrong, width: 1, style: LineStyle.Dotted, labelBackgroundColor: tokens.bgRaised },
      },
      localization: {
        priceFormatter: (price: number) =>
          price.toLocaleString('en-US', {
            minimumFractionDigits: priceDecimals,
            maximumFractionDigits: priceDecimals,
          }),
      },
    })

    const series = chart.addSeries(CandlestickSeries, {
      upColor: tokens.upSolid,
      downColor: tokens.downSolid,
      borderVisible: false,
      wickUpColor: tokens.upSolid,
      wickDownColor: tokens.downSolid,
      priceFormat: { type: 'price', precision: priceDecimals, minMove: 1 / 10 ** priceDecimals },
      // **The strike is never allowed off-screen** (components.md §2.10): a
      // chart that scrolls it out of view has deleted the only line that
      // decides the payoff. Autoscale fits the *visible* bars, which is right
      // over ninety of them and absurd over the two that exist two seconds into
      // a market — a four-dollar range magnifying noise, with the strike above
      // the frame.
      //
      // So the fitted range is widened to contain the strike — and that is all.
      // `CHART_FLOOR_BPS` is a guard against a zero-height pane, not a scale:
      // its comment records the two floors that were tried here and why any
      // floor not taken from the bars on screen flattens a quiet minute.
      autoscaleInfoProvider: (original: () => AutoscaleInfo | null) => {
        const info = original()
        const strike = strikeRef.current
        if (!info?.priceRange || strike === null) return info
        const floor = Math.abs(strike) * (CHART_FLOOR_BPS / 10_000)
        return {
          ...info,
          priceRange: {
            minValue: Math.min(info.priceRange.minValue, strike - floor),
            maxValue: Math.max(info.priceRange.maxValue, strike + floor),
          },
        }
      },
    })

    const primitive = new MarketBandsPrimitive(() => tokens)
    series.attachPrimitive(primitive)

    chartRef.current = chart
    seriesRef.current = series
    primitiveRef.current = primitive

    // The OHLC readout. Follows the crosshair when there is one and falls back
    // to the last bar when the pointer leaves, so the header is never empty.
    const unsubscribe = chart.subscribeCrosshairMove((param) => {
      const bar = param.seriesData.get(series) as CandlestickData<Time> | undefined
      setLegend(bar ? legendOf(bar) : null)
    })

    return () => {
      // `subscribeCrosshairMove` returns void in v5; the handler dies with the
      // chart. Named above only so the intent is readable.
      void unsubscribe
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      primitiveRef.current = null
      priceLinesRef.current = []
      formingRef.current = null
    }
  }, [tokens, priceDecimals])

  // After the chart, and keyed on `tokens` too: a theme change rebuilds the
  // chart above at the default size, and this puts the scaled one back.
  useEffect(() => {
    chartRef.current?.applyOptions({ layout: { fontSize: axisFontPx } })
  }, [axisFontPx, tokens])

  // ---------------------------------------------------------------------
  // History
  // ---------------------------------------------------------------------

  const bars = useMemo(() => history.map(toBar).filter((b): b is CandlestickData<Time> => b !== null), [history])

  // `tokens` is in the deps and is load-bearing. It starts `null`, so on the
  // first render the chart does not exist yet and this returns early; without
  // `tokens` here it would never run again once the chart appeared, and the
  // pane would show only the handful of bars the live index had formed since.
  // That is the same shape of bug as the index series' missing store dependency
  // — an effect that fired once against a thing that was not there yet.
  useEffect(() => {
    const series = seriesRef.current
    const chart = chartRef.current
    if (!series || !chart || bars.length === 0) return
    series.setData(bars as CandlestickData<Time>[])
    formingRef.current = bars[bars.length - 1] ?? null
    // How many bars the series actually holds, on the element. The chart's own
    // state is not reachable from the DOM, and "did the history land" is the
    // question that separates a chart with no data from a chart with a bad
    // viewport — a distinction that cost an hour to make by eye.
    hostRef.current?.setAttribute('data-bars', String(bars.length))

    // **Not `fitContent()`.** Fitting 300 bars into the pane is a wall of
    // three-pixel candles, and fitting *three* is three candles a third of the
    // pane wide; a trading chart opens on a window of a fixed width and lets the
    // user zoom out to the rest. `rollingRange` is that window, and its own
    // comment is why the count is constant rather than clamped to the data.
    //
    // `setData` resets the scale, so this puts the pane somewhere sensible for
    // the frame between the history landing and the viewport effect below
    // running. That effect owns the range from then on — including this one,
    // which it recomputes off the live series rather than off `bars`.
    chart.timeScale().setVisibleLogicalRange(rollingRange(bars.length))
    rangeRef.current = null
  }, [bars, tokens])

  /**
   * Nothing moves the pane while it is a market rather than a window: not the
   * user, and not the chart itself.
   *
   * **`shiftVisibleRangeOnNewBar` is the one that mattered**, and it defaults
   * on. It slides the range one bar right whenever a bar is appended at or past
   * the right edge — which is the whole point of a rolling window and exactly
   * wrong for a pinned one, because a `market` window's right edge is always
   * past the last bar: the rest of the minute has not happened yet. So the
   * pane crept one second to the right every second, the market's own bars
   * marched off the left, and after fifty seconds it showed two candles and a
   * pane of blank. Nothing recovered it, because the *computed* range was
   * still correct — only the applied one had drifted.
   *
   * `applyOptions` rather than a `createChart` argument: the chart is rebuilt
   * only on a theme change, and a timeframe switch must not tear it down and
   * lose the series with it.
   *
   * **It is declared above the viewport effect on purpose, and `rightOffset`
   * is why.** That option is not a preference the chart consults later — it
   * *moves the visible range now*, so the last bar sits that many bars from
   * the right edge. Declared after the viewport effect it ran second on a
   * timeframe change and undid the range that effect had just set: `market`
   * computed the correct `[anchor, expiry]`, this pulled the right edge back
   * onto the newest bar, and the pane showed the last sixty seconds *ending
   * now* instead of the market's own minute. The width was right, which is why
   * it reads as a bigger range outside the market rather than as a broken
   * chart.
   *
   * It then stayed wrong, because the viewport effect re-applies only when its
   * *computed* range changes — and for a pinned market that is not until the
   * next roll. "The chart does not change until a new market starts" is that
   * sentence from the outside.
   *
   * Ordering alone fixes it. `rangeRef` is cleared as well so the next tick
   * re-asserts the range whatever the order is, because an ordering dependency
   * between two effects in one file is exactly what a later edit moves without
   * noticing.
   */
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const locked = timeframe === 'market'
    chart.applyOptions({
      handleScroll: !locked,
      handleScale: !locked,
      timeScale: { shiftVisibleRangeOnNewBar: !locked, rightOffset: locked ? 0 : 6 },
    })
    rangeRef.current = null
  }, [timeframe, tokens])

  // ---------------------------------------------------------------------
  // The market's own minute
  // ---------------------------------------------------------------------

  /**
   * `market` pins the pane to `[anchor_ms, expiry_at_ms]` — the sixty seconds
   * being traded, and nothing either side.
   *
   * The series still holds the full history; only the viewport is narrowed. So
   * the roll costs a range change and not a refetch, `market ⇄ 1s` is free, and
   * a market's first second renders one candle against an empty minute — which
   * is the honest picture of a minute that has just started, not a chart that
   * failed to load.
   *
   * **It reads `series.data()` rather than `bars`, and that is the whole
   * correctness argument.** `bars` is the *fetched history*, frozen at load;
   * every bar after it arrives by `series.update` and is not in that array. So
   * a window into the live half has to be located against what the series
   * actually holds. Deriving it as `history.length + elapsed seconds` looked
   * right and was not: the live bars are one per *frame that arrived*, not one
   * per second, so a few skipped seconds slid the window off the end of the
   * data and the pane rendered two candles and a minute of blank.
   *
   * Recomputing on every tick is what makes that self-correcting: the right
   * edge is `lastIndex + the seconds still to come`, and as bars land the two
   * terms trade off and the edge stays put.
   *
   * **A logical range and not `setVisibleRange`, for two reasons.** The
   * temporal API resolves both endpoints against the data, so it throws
   * outright before the first history lands — the pane rendered its error
   * boundary — and it clamps a `to` that is past the last bar. Clamping is the
   * one thing this must not do: for most of a market the second half of its
   * minute has not happened yet, and the pane has to show that empty half
   * rather than stretch five seconds across the width and jump every tick.
   */
  useEffect(() => {
    const chart = chartRef.current
    const series = seriesRef.current
    if (!chart || !series) return
    const data = series.data() as readonly CandlestickData<Time>[]
    if (data.length === 0) return

    // Every other option is the rolling window the chart has always had, and it
    // is also what `market` falls back to when the market's own span cannot be
    // placed. Restoring it here rather than leaving it to the history effect is
    // not tidiness: `market` and `1s` share one cache entry, so switching
    // between them does not change `history` at all — and a pane that only
    // re-ranged when the data changed stayed stuck on the market's minute.
    const rolling = rollingRange(data.length)
    const range = windowRange(data, timeframe, windowStartMs, windowEndMs) ?? rolling

    const held = rangeRef.current
    if (held === null || held.from !== range.from || held.to !== range.to) {
      rangeRef.current = range
      chart.timeScale().setVisibleLogicalRange(range)
    }

    // The *painted* window, in seconds, on the element — for the same reason
    // `data-bars` is there: the chart's own state is not reachable from the
    // DOM, and "is the pane still on the minute it was pinned to" is a question
    // no screenshot answers. It is read back rather than restated, so a range
    // the chart moved on its own — which is what
    // `shiftVisibleRangeOnNewBar` did, one bar per second, invisibly — shows up
    // as a value that drifts.
    const painted = chart.timeScale().getVisibleRange()
    if (painted) {
      hostRef.current?.setAttribute('data-window', `${painted.from as number}-${painted.to as number}`)
    }
  }, [timeframe, windowStartMs, windowEndMs, bars, index, tokens])


  // ---------------------------------------------------------------------
  // The live bar
  // ---------------------------------------------------------------------

  useEffect(() => {
    const series = seriesRef.current
    const observation = observationFrom(index)
    const price = toNumber(observation)
    const atMs = (index as { observed_at_ms?: unknown } | null)?.observed_at_ms
    if (!series || price === null || typeof atMs !== 'number') return

    const stepMs = TIMEFRAME_MS[timeframe]
    const bucket = (Math.floor(atMs / stepMs) * stepMs) / 1000
    const held = formingRef.current

    const next = nextBar(held, price, bucket)

    // A bar the server has already closed must not be reopened by a late tick.
    if (held && (next.time as number) < (held.time as number)) return

    formingRef.current = next
    series.update(next)
    setLegend((current) => (current === null || current.live ? { ...legendOf(next), live: true } : current))
  }, [index, timeframe])

  // ---------------------------------------------------------------------
  // The market, over the price
  // ---------------------------------------------------------------------

  useEffect(() => {
    const series = seriesRef.current
    if (!series || !tokens) return

    for (const line of priceLinesRef.current) series.removePriceLine(line)
    priceLinesRef.current = []

    strikeRef.current = null
    for (const band of bands) {
      const strike = toNumber(observationFrom(band.strike))
      if (strike === null) continue
      const live = band.marketId === currentMarketId
      // Held for the autoscale, which has to keep this one line in frame.
      if (live) strikeRef.current = strike
      priceLinesRef.current.push(
        series.createPriceLine({
          price: strike,
          // The live market's strike is the line the payoff turns on, so it is
          // the only solid one. Past strikes are dashed and dim: context, not
          // instruction.
          color: live ? tokens.accentSolid : tokens.borderStrong,
          lineWidth: 1,
          lineStyle: live ? LineStyle.Solid : LineStyle.Dashed,
          axisLabelVisible: live,
          title: live ? 'STRIKE' : '',
        }),
      )
    }

    primitiveRef.current?.setBands(bands, currentMarketId)
    // The provider reads `strikeRef` at draw time, so a strike that moved
    // without the data moving — every roll — needs the scale asked again.
    series.priceScale().applyOptions({})
  }, [bands, currentMarketId, tokens])

  const onTimeframe = useCallback(
    (next: Timeframe) => () => onTimeframeChange(next),
    [onTimeframeChange],
  )

  const shown = legend ?? (bars.length > 0 ? legendOf(bars[bars.length - 1] as CandlestickData<Time>) : null)

  return (
    // `relative` so the loading overlay below centres on the chart rather than
    // on whichever ancestor happened to be positioned.
    <div className={cx('relative flex min-h-0 flex-1 flex-col', className)}>
      <div className="flex shrink-0 items-center gap-3 px-2 py-1">
        <div
          ref={timeframeRef}
          role="radiogroup"
          aria-label="timeframe"
          // **Below 768px the timeframes are the whole bar**, and they scroll
          // rather than push. Eight at the 44px hit-target floor are 359px,
          // which fills a 375px phone with nothing to spare and overflows a
          // 360px one; `min-w-0` lets the group shrink and the arrow keys'
          // `.focus()` scrolls the chosen one into view.
          className="ud-scroll flex min-w-0 items-center gap-px"
          onKeyDown={(event) => {
            // One tab stop, arrows to move — the model
            // `radio-group.ts` gives the ticket's two groups. This one had
            // claimed the role since Phase 4 and implemented none of it.
            const to = nextRadioIndex(event.key, TIMEFRAMES.indexOf(timeframe), TIMEFRAMES.length)
            if (to === null) return
            event.preventDefault()
            const next = TIMEFRAMES[to]
            if (!next) return
            onTimeframe(next)()
            timeframeRef.current?.querySelectorAll<HTMLButtonElement>('[role=radio]')[to]?.focus()
          }}
        >
          {TIMEFRAMES.map((option, index) => (
            <button
              key={option}
              type="button"
              role="radio"
              aria-checked={option === timeframe}
              tabIndex={radioTabIndex(index, TIMEFRAMES.indexOf(timeframe))}
              onClick={onTimeframe(option)}
              className={cx(
                'ud-num rounded-xs px-1_5 py-0_5 text-caption transition-colors duration-[var(--ud-duration-instant)]',
                'focus-visible:shadow-focus focus-visible:outline-none',
                // components.md §1's hit-target floor, below 768px only.
                'inline-flex items-center justify-center min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] md:min-h-0 md:min-w-0',
                option === timeframe
                  ? 'bg-bg-active text-text-primary'
                  : 'text-text-tertiary hover:bg-bg-hover hover:text-text-secondary',
              )}
            >
              {option}
            </button>
          ))}
        </div>

        {/* The OHLC readout, as a terminal has it: follows the crosshair, falls
            back to the last bar so the header is never empty. Not below 768px,
            where four full prices are wider than the phone — the price axis
            still labels the last one (components.md §2.10). */}
        {shown ? (
          <div className="ud-num hidden items-baseline gap-2 text-micro md:flex">
            {/*
              **One glyph for the whole readout, not four.**

              The four numbers are green or red together — they carry a single
              fact, "this bar closed above its open" — and until Phase 10 colour
              was the only thing carrying it, which design-system §2.5 forbids.
              A triangle on each of O/H/L/C would repeat one fact four times in a
              12px row; one at the head of the group says it once, in the reading
              order, and is what the colour is already grouping.
            */}
            <span
              className={shown.up ? 'text-up-text' : 'text-down-text'}
              aria-label={shown.up ? 'closed up' : 'closed down'}
            >
              <Glyph name={shown.up ? 'up' : 'down'} label={null} />
            </span>
            <Ohlc label="O" value={shown.open} up={shown.up} decimals={priceDecimals} />
            <Ohlc label="H" value={shown.high} up={shown.up} decimals={priceDecimals} />
            <Ohlc label="L" value={shown.low} up={shown.up} decimals={priceDecimals} />
            <Ohlc label="C" value={shown.close} up={shown.up} decimals={priceDecimals} />
          </div>
        ) : null}

        <span className="ml-auto hidden text-micro text-text-tertiary md:inline">{symbol}</span>
        {/* At every width: a chart that has stopped updating says so. */}
        {stale ? <span className="ml-auto shrink-0 text-micro text-warning-text md:ml-0">STALE</span> : null}
      </div>

      {/* `relative` and a size container, so the underlay fills exactly the
          pane the canvases occupy and scales its type against it. `isolate`
          keeps the two z-indexes below inside this stacking context. */}
      <div className="relative isolate flex min-h-0 flex-1 flex-col [container-type:size]">
        {underlay ? (
          <div className="pointer-events-none absolute inset-0 z-0 grid place-items-center">
            {underlay}
          </div>
        ) : null}
        <div
          ref={hostRef}
          data-stale={stale ? 'true' : undefined}
          data-testid="market-chart"
          // The line desaturates when the socket is down; it does **not** blank,
          // and it does **not** extend with invented bars (§2.10 States).
          className={cx(
            'ud-chart relative z-10',
            // A fixed 240px below 768px (§2.10 Geometry) and the column's
            // whole height above it. It cannot be `flex-1` with a floor at
            // both: on a short single-column viewport the pane is shorter than
            // that floor, and a box shorter than its content overflows rather
            // than clipping — the canvas drew straight over the series rail.
            'h-[calc(240*var(--ud-u))] shrink-0 md:h-auto md:min-h-[calc(240*var(--ud-u))] md:flex-1',
            'data-[stale=true]:opacity-55 data-[stale=true]:saturate-50',
          )}
        />
      </div>

      {loading && bars.length === 0 ? (
        <div className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="text-body-sm text-text-tertiary">loading {timeframe} candles</span>
        </div>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------

interface Legend {
  open: number
  high: number
  low: number
  close: number
  up: boolean
  live?: boolean
}

function legendOf(bar: CandlestickData<Time>): Legend {
  return {
    open: bar.open,
    high: bar.high,
    low: bar.low,
    close: bar.close,
    up: bar.close >= bar.open,
  }
}

function Ohlc({
  label,
  value,
  up,
  decimals,
}: {
  label: string
  value: number
  up: boolean
  decimals: number
}) {
  return (
    <span className="flex items-baseline gap-0_5">
      <span className="text-text-tertiary">{label}</span>
      <span className={up ? 'text-up-text' : 'text-down-text'}>
        {value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      </span>
    </span>
  )
}

/**
 * The rolling window: `VISIBLE_BARS` of history and a few bars of headroom on
 * the right, as a **constant span**.
 *
 * **`from` is allowed to go negative, and that is the whole of it.** It used to
 * be clamped to zero, which reads as caution and is a bug: the time scale sets
 * `barSpacing = width / range.count()`, so a series shorter than the window was
 * stretched across the pane and bar width became a function of how much history
 * the venue happened to hold. The index buffer is an hour, so `5m`/`15m`/`1h`
 * came back with about thirteen, five and three bars and drew candles a fifth of
 * the pane wide, clustered on the left with dead space at the right edge — while
 * `1s` at the same moment was correct, because it had its ninety.
 *
 * A negative `from` only enlarges the count. The library places the empty slots
 * to the left, the newest bar keeps the right edge, and every timeframe draws at
 * one bar width — which is the honest picture of a chart with three hours of
 * history behind it.
 */
export function rollingRange(barCount: number): { from: number; to: number } {
  return { from: barCount - VISIBLE_BARS, to: barCount + RIGHT_HEADROOM_BARS }
}

/**
 * The `market` option's pinned range, or `null` when there is not one to draw.
 *
 * Every reason a window cannot be drawn resolves to the same answer — fall back
 * to the rolling window — so they are collected here rather than left as four
 * guards at the call site: a different timeframe, a caller that passed no span,
 * a span whose endpoints cannot be placed on the series, and a span that is not
 * a forward interval.
 *
 * That last check is the one that earns its keep. `setVisibleLogicalRange`
 * *asserts* `from <= to` and throws, and the chart is inside an error boundary,
 * so one inverted range does not misdraw a pane — it deletes it.
 */
export function windowRange(
  bars: readonly CandlestickData<Time>[],
  timeframe: Timeframe,
  windowStartMs: number | undefined,
  windowEndMs: number | undefined,
): { from: number; to: number } | null {
  if (timeframe !== 'market' || windowStartMs === undefined || windowEndMs === undefined) return null
  const stepSeconds = TIMEFRAME_MS[timeframe] / 1000
  const from = logicalIndexAt(bars, windowStartMs / 1000, stepSeconds)
  const to = logicalIndexAt(bars, windowEndMs / 1000, stepSeconds)
  if (from === null || to === null) return null
  if (!(from < to)) return null
  return { from: from - 0.5, to: to + 0.5 }
}

/**
 * Where an instant falls on the series' own index, extrapolating past both ends,
 * or `null` if it cannot be placed at all.
 *
 * **Inside the data it interpolates against the neighbouring bar, and outside
 * it extrapolates by `step`.** It used to do the second thing everywhere, on
 * the stated assumption that the series is a dense grid one `step` apart — and
 * that assumption is not the chart's to make. A series with a hole in it made
 * `i + (at − t) / step` count *seconds* where the chart counts *bars*, so an
 * instant a minute past a 25-second hole resolved to logical 322 on a 303-bar
 * series: off the end entirely, and the pane painted an arbitrary window of
 * empty space. Measured, not theorised — `data-debug` reported
 * `len 303, span 326, gaps 1, biggest 25, range.from 322.5`.
 *
 * Interpolating between the two bars an instant actually falls between keeps
 * the result inside `[0, lastIndex]` however the data is shaped, and reduces to
 * the old arithmetic exactly when the grid is dense. The extrapolation past the
 * last bar is untouched and needs no density: there are no bars out there, so
 * "n steps beyond the end" is the only thing it could mean, and it is what the
 * chart draws.
 *
 * Both halves matter: the window's right edge is in the future for most of a
 * market's life, and its left edge drifts past the fetched history within
 * minutes, because history is fetched per `(symbol, interval)` and the live
 * bars after it arrive by `series.update` rather than by growing `bars`.
 *
 * Fractional on purpose — a half-bar of margin at each edge reads better than
 * candles touching the frame.
 *
 * **`null` rather than a fallback index, and the fallback was a real defect.**
 * This returned `0` when it could not place an instant, which is a number and
 * therefore indistinguishable from an answer. A `NaN` instant — every
 * comparison against it is false, so it fell through every branch — placed one
 * endpoint at `0` and left the other wherever it belonged, and
 * `setVisibleLogicalRange` asserts `from <= to`. The chart threw, the error
 * boundary took the whole panel, and the pane read `this panel stopped
 * rendering`. A function that cannot answer has to say so.
 */
export function logicalIndexAt(
  bars: readonly CandlestickData<Time>[],
  atSeconds: number,
  stepSeconds: number,
): number | null {
  const lastIndex = bars.length - 1
  const first = bars[0]?.time as number | undefined
  const last = bars[lastIndex]?.time as number | undefined
  if (first === undefined || last === undefined) return null
  if (!Number.isFinite(atSeconds) || !Number.isFinite(stepSeconds) || stepSeconds <= 0) return null
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null
  if (atSeconds <= first) return (atSeconds - first) / stepSeconds
  if (atSeconds >= last) return lastIndex + (atSeconds - last) / stepSeconds
  for (let i = lastIndex; i >= 0; i--) {
    const t = bars[i]?.time as number
    if (t > atSeconds) continue
    // The gap to the *next bar*, not the nominal step. On a dense series they
    // are the same number; across a hole they are not, and this is the one
    // place the difference decides whether the answer is a bar index or a
    // count of seconds wearing one.
    const next = bars[i + 1]?.time as number | undefined
    const width = next === undefined || next <= t ? stepSeconds : next - t
    return i + (atSeconds - t) / width
  }
  return null
}

/**
 * The forming bar after one tick — extend the current bucket, or open the next.
 *
 * **A new bar opens at the previous bar's close**, so the series is contiguous
 * rather than a row of dojis. That is the half that was already right; the half
 * that was not is `high` and `low`, which were taken from the incoming price
 * alone. On any move between buckets that produced a bar whose high was below
 * its open — the legend read `O 65,035.73 H 65,035.33` on a real screen — and a
 * candlestick renderer given `high < open` draws a body it has no room for.
 *
 * The open is an endpoint of the bar like any other, so it belongs inside the
 * extremes from the first tick. `high` and `low` then only ever widen.
 *
 * Pure, and exported for that reason: the effect that calls it lives beside a
 * canvas and a chart library, and this is arithmetic.
 */
export function nextBar(
  held: CandlestickData<Time> | null,
  price: number,
  bucketSeconds: number,
): CandlestickData<Time> {
  // A tick inside the bucket that is forming extends it. Never `setData` —
  // that would re-send the whole history at the index's 5 Hz.
  if (held && (held.time as number) === bucketSeconds) {
    return {
      time: held.time,
      open: held.open,
      high: Math.max(held.high, price),
      low: Math.min(held.low, price),
      close: price,
    }
  }
  const open = held?.close ?? price
  return {
    time: bucketSeconds as UTCTimestamp,
    open,
    high: Math.max(open, price),
    low: Math.min(open, price),
    close: price,
  }
}

/**
 * One API candle as a bar.
 *
 * Index candles carry plain decimal strings rather than a `{raw, expo}`
 * observation, so this is the one place a price is parsed with `Number`. It is
 * chart geometry and nothing compares it: the tie rule never touches this
 * series, and the settlement value comes off the market object (§2.10 data
 * rules 1).
 */
function toBar(candle: ChartCandle): CandlestickData<Time> | null {
  const { open_time_ms, open, high, low, close } = candle
  if (typeof open_time_ms !== 'number') return null
  const values = [open, high, low, close].map((v) => (typeof v === 'string' ? Number(v) : NaN))
  if (values.some((v) => !Number.isFinite(v))) return null
  return {
    time: (open_time_ms / 1000) as UTCTimestamp,
    open: values[0] as number,
    high: values[1] as number,
    low: values[2] as number,
    close: values[3] as number,
  }
}

// ---------------------------------------------------------------------------

export interface Tokens {
  bgSurface: string
  bgRaised: string
  borderSubtle: string
  borderDefault: string
  borderStrong: string
  textTertiary: string
  textSecondary: string
  accentSolid: string
  upSolid: string
  downSolid: string
  upDepth: string
  downDepth: string
  mono: string
}

/**
 * Resolved token values, for the canvas.
 *
 * `getComputedStyle(el).getPropertyValue('--ud-color-…')` rather than a typed
 * hex: a raw colour in a component opts out of the light theme *and* the
 * `[data-cvd]` remap, and `check-design-tokens.mjs` fails the build on one.
 */
function readTokens(element: HTMLElement): Tokens {
  const style = getComputedStyle(element)
  const get = (name: string) => style.getPropertyValue(name).trim()
  const mono = get('--ud-font-mono') || 'monospace'
  return {
    bgSurface: get('--ud-color-bg-surface'),
    bgRaised: get('--ud-color-bg-raised'),
    borderSubtle: get('--ud-color-border-subtle'),
    borderDefault: get('--ud-color-border-default'),
    borderStrong: get('--ud-color-border-strong'),
    textTertiary: get('--ud-color-text-tertiary'),
    textSecondary: get('--ud-color-text-secondary'),
    accentSolid: get('--ud-color-accent-solid'),
    upSolid: get('--ud-color-up-solid'),
    downSolid: get('--ud-color-down-solid'),
    upDepth: get('--ud-color-up-depth'),
    downDepth: get('--ud-color-down-depth'),
    mono,
  }
}

/**
 * A size token in pixels. The token is a `calc()` over the scale unit, which
 * `getPropertyValue` returns unevaluated — so it is applied to a probe and the
 * probe's computed `font-size`, which the browser has evaluated, is read back.
 */
function pxOf(element: HTMLElement, token: string, fallback: number): number {
  const probe = document.createElement('span')
  probe.style.position = 'absolute'
  probe.style.visibility = 'hidden'
  probe.style.fontSize = `var(${token})`
  element.appendChild(probe)
  const px = Number.parseFloat(getComputedStyle(probe).fontSize)
  probe.remove()
  return Number.isFinite(px) && px > 0 ? px : fallback
}
